import * as React from 'react'

import {
  TagColor as Color,
  OverflowContextProvider,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

interface Props {
  legalEntity: string
}

const hashCode = (legalEntity: string): number => {
  let value = 0
  for (let i = 0; i < legalEntity.length; i++) {
    value += legalEntity.charCodeAt(i)
  }

  return value % 5
}

const tagColour = (legalEntity: string, allEntitiesText: string): Color | undefined => {
  const entitiesColors: Record<number, Color> = {
    0: Color.lime,
    1: Color.malibu,
    2: Color.viridian,
    3: Color.purple,
    4: Color.lime,
  }

  if (legalEntity === allEntitiesText) {
    return Color.grey
  }

  if (isCountryTag(legalEntity)) {
    return Color.champagne
  }

  const hash = hashCode(legalEntity)
  return entitiesColors[hash]
}

const isCountryTag = (legalEntity: string) => {
  return (
    legalEntity ==
      i18n.t('documents.document_templates.all_available_variables_list.entities.countries.es') ||
    legalEntity ==
      i18n.t('documents.document_templates.all_available_variables_list.entities.countries.fr') ||
    legalEntity ==
      i18n.t('documents.document_templates.all_available_variables_list.entities.countries.de')
  )
}

const assertCellEllipsis = (text: string, maxCharacters = 8): boolean => {
  return text.length > maxCharacters
}

const showTooltip = (
  toggle: (value: boolean) => () => void,
  open: boolean,
  text: string,
  ref: {
    current: null | HTMLElement
  }
) => {
  return text.length > 15 ? (
    <OverflowContextProvider childrenRef={ref} value={text}>
      <Tooltip.Alt
        attachment="bottom center"
        targetAttachment="top center"
        toggle={toggle}
        open={open}
        label={text}
      >
        <Text.Normal
          size="200"
          weight="medium"
          whiteSpace="nowrap"
          innerRef={ref}
          ellipsis={assertCellEllipsis(text || '')}
        >
          {text}
        </Text.Normal>
      </Tooltip.Alt>
    </OverflowContextProvider>
  ) : (
    <OverflowContextProvider childrenRef={ref} value={text}>
      <Text.Normal
        size="200"
        weight="medium"
        whiteSpace="nowrap"
        innerRef={ref}
        ellipsis={assertCellEllipsis(text || '')}
      >
        {text}
      </Text.Normal>
    </OverflowContextProvider>
  )
}

const MainLegalEntity = ({ legalEntity }: Props) => {
  const ALL_ENTITIES_STRING = i18n.t(
    'documents.document_templates.all_available_variables_list.entities.all'
  )
  const [openLegalEntity, toggleLegalEntity] = useToggle(false)
  const legalEntityReference = React.useRef(null)

  const legalEntityName = legalEntity || ALL_ENTITIES_STRING

  return (
    <Tag type={tagColour(legalEntityName, ALL_ENTITIES_STRING)}>
      {showTooltip(toggleLegalEntity, openLegalEntity, legalEntityName, legalEntityReference)}
    </Tag>
  )
}

export default MainLegalEntity
